import React, { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { CheckCircleFilled, LeftOutlined } from '@ant-design/icons'
import { AutoComplete, Form, Input } from 'antd'
import styled from 'styled-components'
import { Card as GlobalCard, Container as GlobalContainer } from '../../styles'
import QuiqupLogo from '../../assets/quiqup-logo.png'
import NotFoundCard from '../not-found/not-found-card'
import PinMap, { MapTarget, MoveSource } from '../pin-confirm/pin-map'
import AddressSearchInput from '../pin-confirm/address-search-input'
import {
  ConfirmPinAddress,
  useConfirmPinLocation,
} from '../../hooks/useConfirmPinLocation'
import { useCitiesForEmirate } from '../../hooks/useCitiesForEmirate'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { AddressDetail } from '../../hooks/useAddressDetail'
import {
  PinLinkAddress,
  usePinLinkContext,
} from '../../hooks/usePinLinkContext'
import { Coordinates } from '../../types/Order'
import theme from '../../theme'
import UAECities from '../../utils/uaeCities.json'
import {
  AddressFields,
  AddressFormWrapper,
  BackButton,
  BottomSheet,
  ConfirmButton,
  FormHeader,
  MapArea,
  PageContainer,
  StatusMessage,
  Subtitle,
  Title,
} from '../pin-confirm/pin-confirm-page-styled'

// Default view when the message carries no best-guess point. Centred on Dubai,
// city-level zoom, so the customer can pan to their neighbourhood.
const UAE_DEFAULT_CENTER: Coordinates = { lat: 25.2048, lng: 55.2708 }
const FALLBACK_ZOOM = 11
const GUESS_ZOOM = 17

// Emirate typeahead options. AutoComplete (not Select) so the customer can
// also type a value that isn't in the list — same UX as Quiqdash.
const EMIRATE_OPTIONS = UAECities.map((city) => ({ value: city }))

// Per-field character cap. Matches the geocoding backend's 200-char limit on
// each address field so the UI can't submit something the server would 400 on.
const FIELD_MAX_LENGTH = 200

// Debounce applied to the emirate value before it drives the Platform cities
// lookup, so free-text typing settles on a value rather than firing one
// request per keystroke. ~350ms: long enough to coalesce typing, short enough
// that suggestions feel responsive once the customer stops.
const EMIRATE_DEBOUNCE_MS = 350

/**
 * A required-field rule that also rejects whitespace-only input. `required:
 * true` renders Ant's visual required marker (the red asterisk) up front; the
 * trim-aware validator is what actually enforces it, because the wire payload
 * trims and would otherwise silently drop a spaces-only value with no error.
 */
function requiredTrimmedRule(message: string) {
  return {
    required: true,
    validator: (_rule: unknown, value?: string) =>
      value?.trim() ? Promise.resolve() : Promise.reject(new Error(message)),
  }
}

/**
 * Case-insensitive `filterOption` predicate shared by the Emirate and Area
 * AutoCompletes so their built-in typeahead filtering stays identical. Ant's
 * `filterOption` shows an option when this returns true; both fields use plain
 * `{ value: string }` options, so we match the typed input against that value.
 */
function caseInsensitiveFilterOption(
  input: string,
  option?: { value?: string }
): boolean {
  return (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
}

/**
 * Step-2 form values, in customer-facing terms. The UI -> wire mapping (e.g.
 * building name -> `floor`, emirate -> `state`) is applied by
 * `useConfirmPinLocation`, so this component only ever speaks in labels.
 */
type AddressFormValues = {
  buildingName?: string
  area?: string
  emirate?: string
  apartmentNumber?: string
}

/** Every field on the step-2 form, for iterating over the prefill. */
const ADDRESS_FIELD_NAMES: (keyof AddressFormValues)[] = [
  'emirate',
  'area',
  'buildingName',
  'apartmentNumber',
]

/** A field is "empty" — and so safe to prefill — when it has no visible text. */
function isBlank(value?: string) {
  return !value || !value.trim()
}

const SuccessCard = styled(GlobalCard)`
  justify-content: center;
  align-items: center;
  text-align: center;

  .success-icon {
    font-size: 64px;
    color: ${theme.cornFlower500};
    margin-bottom: 16px;
  }

  h2 {
    font-size: 1.5rem;
    color: ${theme.midnight500};
    font-weight: 300;
    margin-bottom: 8px;
  }

  p {
    color: ${theme.slate500};
    font-size: 0.9rem;
  }

  .powered-by-quiqup {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    margin-top: 24px;
  }
`

function SuccessView() {
  return (
    <GlobalContainer data-testid="pin-confirm-success">
      <SuccessCard>
        <CheckCircleFilled className="success-icon" />
        <h2>Thank you!</h2>
        <p>
          We&apos;ve saved your delivery location and your courier will use it
          for your next delivery.
        </p>
        <div className="powered-by-quiqup">
          Delivered by <img alt="Quiqup" src={QuiqupLogo} width={140} />
        </div>
      </SuccessCard>
    </GlobalContainer>
  )
}

/**
 * Public, no-login page reached from a WhatsApp link (R047, story sc-26879).
 * The customer drags a map pin to their exact door and confirms; the pin is
 * sent to the geocoding service which resolves the address and closes the case.
 *
 * Route: /pin/:token
 *   :token  opaque signed string carrying identity — carried through verbatim,
 *           never decoded or validated client-side. Also the credential for the
 *           two public geocoding reads below.
 *   ?lat= & ?lng=  optional current best-guess point for the initial pin.
 *
 * sc-27850 adds a head start on both steps: the link's own context (best-guess
 * point + whatever address the service already holds) positions the pin and
 * seeds the form, and a Google Places address search on the map step flies the
 * pin to a place the customer names and fills in what that place knows about
 * itself — including its building name. Both are strictly optional: every
 * failure path degrades to the original "drag the pin, type the address" flow,
 * and neither may ever gate the map or the Confirm button.
 */
export default function PinConfirmPage() {
  const { token } = useParams()
  const [searchParams] = useSearchParams()

  // What the geocoding service already knows about this link. Never surfaces an
  // error and never blocks the flow — see `usePinLinkContext`.
  const { context: linkContext } = usePinLinkContext(token)

  const parsedLat = parseFloat(searchParams.get('lat') ?? '')
  const parsedLng = parseFloat(searchParams.get('lng') ?? '')
  const hasQueryGuess = Number.isFinite(parsedLat) && Number.isFinite(parsedLng)

  // Pin precedence: the service's best-guess point beats the link's ?lat/?lng
  // (the service holds the current answer; the query string is a snapshot from
  // whenever the message was built), and both beat Dubai centre. Zoom follows: a
  // real point means we know roughly where the door is, so show door level; only
  // the bare fallback stays at city zoom to pan from.
  const contextPoint = linkContext.coordinates
  const bestGuess: Coordinates | null =
    contextPoint ?? (hasQueryGuess ? { lat: parsedLat, lng: parsedLng } : null)
  const bestGuessCenter: Coordinates = bestGuess ?? UAE_DEFAULT_CENTER
  const bestGuessZoom = bestGuess ? GUESS_ZOOM : FALLBACK_ZOOM

  // The view Leaflet is created on, frozen on the first render. The link-context
  // read is optional and unbounded in time, so the map must never wait on it —
  // it opens on whatever we already knew (?lat/?lng, else Dubai) and the effect
  // below flies it to the service's point when that arrives. Frozen rather than
  // passed through live because `PinMap`'s `center`/`zoom` are the opening view
  // only: letting `zoom` change would make react-leaflet fire its own `setZoom`
  // jump racing that fly.
  const [openingView] = useState<MapTarget>(() => ({
    center: bestGuessCenter,
    zoom: bestGuessZoom,
  }))

  // Where the map has been *asked* to go: an address the customer picked from
  // search, or the service's best-guess point once it lands, at door-level zoom.
  // Strictly separate from `movedCoords` below, which is what the map *reports*.
  // Feeding reported coordinates back in as the requested centre would make
  // every move request another move — a pan feedback loop.
  const [target, setTarget] = useState<MapTarget | null>(null)
  // The centre the map last settled on, whatever moved it. `null` until it does,
  // and reset whenever a fresh target supersedes it.
  const [movedCoords, setMovedCoords] = useState<Coordinates | null>(null)
  // Whether the customer has taken control of the pin themselves — set only by a
  // drag or a search pick, never by a settle Leaflet initiated on its own (a soft
  // keyboard opening resizes the window, and Leaflet answers that with a pan and
  // a `moveend`). "The map reported a centre" is NOT the same claim, so it must
  // not be used as one: doing so lets a keyboard tap silently pass for the
  // customer having placed their pin.
  const [customerMoved, setCustomerMoved] = useState(false)
  // The coordinate that gets submitted. A drag is always the freshest answer, so
  // it wins over a search pick, which wins over the best point we know. That last
  // term is derived, not frozen: the service's point becomes the answer the
  // moment it arrives, so confirming in the window before it lands can never
  // submit the fallback the map merely opened on.
  const coords: Coordinates = movedCoords ?? target?.center ?? bestGuessCenter

  const handleMapChange = (next: Coordinates, source: MoveSource) => {
    if (source === 'drag') {
      setCustomerMoved(true)
    }
    setMovedCoords(next)
  }

  // Fly to the service's best-guess point when the (optional, unbounded) context
  // read lands after the map has already opened — the same `target` channel a
  // search pick uses, so the pin ends up there and that point is what submits.
  // Skipped once the customer has dragged or picked: their answer is fresher
  // than the service's guess and must never be yanked out from under them.
  useEffect(() => {
    if (!contextPoint || customerMoved || target) {
      return
    }

    if (
      contextPoint.lat === openingView.center.lat &&
      contextPoint.lng === openingView.center.lng
    ) {
      // Already the view Leaflet was created on — nowhere to fly.
      return
    }

    // Drop any centre the map has reported so far. It can only be the opening
    // view (the customer has not touched anything), and leaving it in place
    // would outrank the point we are flying to in the `coords` precedence.
    setMovedCoords(null)
    setTarget({ center: { ...contextPoint }, zoom: GUESS_ZOOM })
  }, [contextPoint, customerMoved, target, openingView])

  // Address components from the last search pick. Held separately from the link
  // context so the prefill precedence below stays explicit. Google Places knows
  // a building's own name, so this can carry all four fields — see
  // `handleSelectAddress`.
  const [searchAddress, setSearchAddress] = useState<PinLinkAddress>({})
  // Values the customer typed, snapshotted while the form is still mounted (see
  // `handleBack`). Needed because Ant's per-field "touched" flag lives on the
  // mounted Field and is lost when step 2 unmounts, while the values survive —
  // without this snapshot a later search pick could not tell a value the
  // customer typed from one we prefilled, and would overwrite their typing.
  //
  // A field's PRESENCE as a key is what records "the customer touched this";
  // clearing a field is touching it, and an emptied field is stored as `''`
  // rather than left `undefined` so it stays distinguishable from a field they
  // never went near.
  const [typedValues, setTypedValues] = useState<AddressFormValues>({})

  // Step 1 = pin picker; step 2 = structured-address form. Mirrors the
  // update-address-modal two-phase flow (pin -> form).
  const [pinConfirmed, setPinConfirmed] = useState(false)
  const [form] = Form.useForm<AddressFormValues>()
  const mutation = useConfirmPinLocation()

  // Prefill precedence, per field: what the customer typed (including a value
  // they deliberately cleared, hence `??` and not `||`) beats a search pick,
  // which beats what the service already held.
  //
  // Emirate, Area and Building name take all three sources since the move to
  // Places: a picked place carries its own building name, so Building name is no
  // longer link-context-or-manual only. Which of them a given pick actually
  // fills is up to the place — a plain street address supplies only the emirate.
  //
  // Apartment / villa number takes only two: a pick never fills it (see
  // `handleSelectAddress`), so it is the customer's own answer or the merchant's.
  const prefillValues = useMemo<AddressFormValues>(
    () => ({
      emirate:
        typedValues.emirate ??
        searchAddress.emirate ??
        linkContext.address.emirate,
      area: typedValues.area ?? searchAddress.area ?? linkContext.address.area,
      buildingName:
        typedValues.buildingName ??
        searchAddress.buildingName ??
        linkContext.address.buildingName,
      apartmentNumber:
        typedValues.apartmentNumber ?? linkContext.address.apartmentNumber,
    }),
    [typedValues, searchAddress, linkContext.address]
  )

  // Seeds step 2 whenever the prefill changes while the form is open — the
  // first mount is covered by `initialValues`, and this covers link context
  // that lands late. Only ever fills a field that is currently blank AND that
  // the customer has not touched, so nothing they wrote is overwritten.
  //
  // Both touched tests are needed, and they cover different windows.
  // `isFieldTouched` is the live one, and the only thing that can see a field the
  // customer emptied *without* leaving step 2 — a blank value alone cannot tell
  // "never filled" from "deliberately deleted", so a late-landing read would
  // otherwise write the service's word over their deletion. It does not survive
  // the step-2 unmount, which is exactly what `typedValues` is for.
  //
  // Accepted limitation of the blank test: because `useForm` keeps field values
  // across the step-2 unmount, a field an EARLIER pick prefilled is no longer
  // blank, so a later pick cannot correct it — going back and picking the Burj
  // Khalifa district after the tower keeps the tower's Building name beside the
  // district's coordinate. Reviewed under sc-27850 and deliberately kept: the
  // customer editing the field is the intended way to correct a prefill.
  useEffect(() => {
    if (!pinConfirmed) {
      return
    }

    const current = form.getFieldsValue()
    const patch: AddressFormValues = {}

    ADDRESS_FIELD_NAMES.forEach((name) => {
      if (
        isBlank(current[name]) &&
        !(name in typedValues) &&
        !form.isFieldTouched(name) &&
        prefillValues[name] !== undefined
      ) {
        patch[name] = prefillValues[name]
      }
    })

    if (Object.keys(patch).length > 0) {
      form.setFieldsValue(patch)
    }
  }, [pinConfirmed, prefillValues, typedValues, form])

  // Watch the emirate field so the Area typeahead can offer that emirate's
  // cities. `useWatch` re-renders on every value change (including each
  // keystroke of free-text), so debounce it before it drives the cities lookup:
  // otherwise typing "Dubai" would fire five (mostly 404) requests against the
  // internal Platform API from this public, unauthenticated page.
  const selectedEmirate = Form.useWatch('emirate', form)
  const debouncedEmirate = useDebouncedValue(
    selectedEmirate,
    EMIRATE_DEBOUNCE_MS
  )
  const { cities } = useCitiesForEmirate(debouncedEmirate)
  // Area suggestions sourced from the Quiqup Platform places API. Free-text is
  // still accepted (AutoComplete allows a value not in the list) — these are
  // advisory suggestions only. Memoised on `cities` (a stable react-query data
  // reference) so we don't hand AutoComplete a fresh options array on every
  // keystroke re-render from the emirate `useWatch`.
  const areaOptions = useMemo(
    () => cities.map((name) => ({ value: name })),
    [cities]
  )

  const expiredView = (
    <NotFoundCard
      title="This link has expired"
      subTitle="Please use the link from your most recent delivery message, or contact our support team."
    />
  )

  // A missing token means the URL is malformed; a 4xx means the signed token
  // was rejected (expired/invalid). Either way the link is unusable.
  if (!token) {
    return expiredView
  }

  if (mutation.isError && mutation.error?.kind === 'expired') {
    return expiredView
  }

  if (mutation.isSuccess) {
    return <SuccessView />
  }

  const isGenericError = mutation.isError && mutation.error?.kind === 'error'

  // Step 1 -> step 2: reveal the address form. The map stays mounted above so
  // the customer can still see (and re-drag) their pin while typing.
  const handleConfirmPin = () => {
    setPinConfirmed(true)
  }

  // Step 2 -> step 1: back to the map so the customer can re-drag the pin or
  // search again. The `form` instance (from useForm) retains field values across
  // the unmount/remount, so anything already typed is still there on return.
  //
  // Ant's "touched" flags do NOT survive that unmount, so snapshot which fields
  // the customer actually typed into while they are still mounted. That snapshot
  // is what stops a later search pick from overwriting their own words.
  const handleBack = () => {
    const values = form.getFieldsValue()
    const typed: AddressFormValues = {}

    ADDRESS_FIELD_NAMES.forEach((name) => {
      if (form.isFieldTouched(name)) {
        // Normalised to `''`, never left `undefined`, so that CLEARING a field
        // records as touched-and-empty. `undefined` in here would read as "never
        // typed" below and re-prefill the value the customer just deleted, and
        // would wipe an earlier genuine value out of the snapshot on merge.
        // (Today rc-select's × lands on `''` anyway — it fires `undefined` then
        // immediately `''` from the combobox search reset — but that is an
        // internal detail of the version we happen to be on, not a contract.)
        typed[name] = values[name] ?? ''
      }
    })

    setTypedValues((previous) => ({ ...previous, ...typed }))
    setPinConfirmed(false)
  }

  // A search pick, already resolved through Places detail by the search input.
  //
  // A pick is a request to move the map, not a report of where it is: it sets
  // `target` (which flies the pin at door-level zoom) and drops any earlier drag
  // so the pick becomes the current answer. A drag after this reports a fresh
  // `movedCoords` and wins again.
  const handleSelectAddress = (detail: AddressDetail) => {
    // `coordinates` is null whenever the service could not resolve a point — a
    // documented 200 answer, not an error. Then the pin is simply not moved:
    // there is nowhere to move it to, and none of the pin state may be touched.
    // In particular `customerMoved` stays as it was, because the customer has
    // NOT positioned anything — setting it would disarm the link-context fly on
    // the strength of a pick that went nowhere.
    if (detail.coordinates) {
      setMovedCoords(null)
      setCustomerMoved(true)
      // A fresh object per pick, deliberately: `PinMap` flies on target identity,
      // so re-picking the same suggestion after dragging away flies back to it
      // instead of being swallowed as "no change". Door-level zoom: a picked
      // address is a specific building, so show the customer the building.
      setTarget({ center: { ...detail.coordinates }, zoom: GUESS_ZOOM })
    }

    // Whatever the place knows, whether or not it had a point. Replaced rather
    // than merged: the latest pick is the current answer, and the prefill effect
    // applies these to blank, untyped fields only.
    //
    // Apartment / villa number is deliberately NOT carried across. Google's
    // subpremise for a searched place is the unit that *business* occupies, not
    // the customer's door, so prefilling it hands them a wrong value to confirm
    // (captain's call, sc-27850). The service is being changed in parallel to
    // stop sending `flat` here; dropping the mapping is correct either way.
    // Link context still prefills it — that value came from the merchant about
    // this customer.
    setSearchAddress({
      emirate: detail.address.emirate,
      area: detail.address.area,
      buildingName: detail.address.buildingName,
    })
  }

  // Step 2 submit: the form's own validation gates this (Ant only calls
  // onFinish once required fields pass). Pass the values in UI terms; the hook
  // maps them to the wire fields and drops any left blank.
  const handleSubmitAddress = (values: AddressFormValues) => {
    const address: ConfirmPinAddress = {
      buildingName: values.buildingName,
      area: values.area,
      emirate: values.emirate,
      apartmentNumber: values.apartmentNumber,
    }

    mutation.mutate({ token, coordinates: coords, address })
  }

  return (
    <PageContainer data-testid="pin-confirm-page">
      <MapArea>
        {/* Rendered immediately and unconditionally. Holding the map back until
            the link-context read settled would open it on the best point we know
            (no mid-look jerk), but that read has no time bound — `retry: false`
            caps retries, not the latency of one in-flight fetch — and the bottom
            sheet is not gated, so a stalled request would leave the customer
            confirming a pin they never saw. The map therefore opens on
            `openingView` and flies to the service's point when it arrives. */}
        <PinMap
          center={openingView.center}
          zoom={openingView.zoom}
          target={target}
          onChange={handleMapChange}
        />
      </MapArea>
      <BottomSheet>
        {pinConfirmed ? (
          <>
            <FormHeader>
              <Title>Add your delivery address</Title>
              <BackButton
                type="text"
                size="small"
                icon={<LeftOutlined />}
                onClick={handleBack}
                // Lock Back while a submit is in flight: leaving for the map
                // then having the request resolve would jump the customer to
                // the success screen unexpectedly.
                disabled={mutation.isLoading}
                data-testid="back-to-map-button"
                aria-label="Back to map"
              >
                Back
              </BackButton>
            </FormHeader>
            <Subtitle>
              Please give us the full address so your courier can find you.
            </Subtitle>
            {isGenericError ? (
              <StatusMessage error role="alert" data-testid="pin-confirm-error">
                Something went wrong. Please check your connection and try
                again.
              </StatusMessage>
            ) : null}
            <AddressFormWrapper>
              <Form<AddressFormValues>
                name="pin-address-form"
                layout="vertical"
                form={form}
                // Seeds the fields on first open with whatever the service
                // already knows (and any search pick made before opening).
                // Every value stays fully editable — these are Inputs and
                // AutoCompletes, not read-only displays — and the effect above
                // keeps a late-arriving prefill from clobbering typed text.
                initialValues={prefillValues}
                onFinish={handleSubmitAddress}
                // All four fields are required, `AddressFields` scrolls at
                // 40vh, and Confirm sits outside that box — so on a phone the
                // blocking error for a field further down (Apartment / villa
                // number is last) renders out of sight and the button reads as
                // dead. Scroll the first offending field into view instead.
                scrollToFirstError
                autoComplete="on"
                data-testid="pin-address-form"
              >
                <AddressFields>
                  {/* Field order (top-down) per product: Emirate -> Area ->
                      Building name -> Apartment/villa. */}
                  <Form.Item
                    name="emirate"
                    label="Emirate"
                    rules={[requiredTrimmedRule('Please enter the emirate')]}
                  >
                    {/* AutoComplete (not Select): searchable typeahead over the
                        UAE emirates list, but the customer can also type an
                        emirate that isn't in the list ("not in list" allowed).
                        `filterOption` makes the built-in list case-insensitively
                        searchable. */}
                    <AutoComplete
                      options={EMIRATE_OPTIONS}
                      placeholder="Emirate"
                      allowClear
                      // Matches the geocoding backend's 200-char field cap so
                      // the UI can't submit a value the server would 400 on.
                      maxLength={FIELD_MAX_LENGTH}
                      data-testid="emirate-input"
                      filterOption={caseInsensitiveFilterOption}
                    />
                  </Form.Item>
                  <Form.Item
                    name="area"
                    label="Area"
                    rules={[requiredTrimmedRule('Please enter the area')]}
                  >
                    {/* AutoComplete whose suggestions are the selected
                        emirate's cities ("areas"), sourced from the internal
                        Quiqup Platform places API
                        (`GET {QUIQUP_PLATFORM_URL}/countries/AE/states/{emirate}/cities`,
                        see `useCitiesForEmirate`) — NOT Google Places. Options
                        refresh when the emirate changes. Free-text is still
                        accepted (a value not in the list is allowed, Nadim's
                        requirement); `filterOption` makes the list
                        case-insensitively searchable, matching the Emirate
                        field. */}
                    <AutoComplete
                      options={areaOptions}
                      placeholder="Area"
                      allowClear
                      maxLength={FIELD_MAX_LENGTH}
                      data-testid="area-input"
                      filterOption={caseInsensitiveFilterOption}
                    />
                  </Form.Item>
                  <Form.Item
                    name="buildingName"
                    label="Building name"
                    rules={[
                      requiredTrimmedRule('Please enter the building name'),
                    ]}
                  >
                    <Input
                      placeholder="Building name"
                      allowClear
                      maxLength={FIELD_MAX_LENGTH}
                      autoComplete="organization"
                      data-testid="building-name-input"
                    />
                  </Form.Item>
                  <Form.Item
                    name="apartmentNumber"
                    label="Apartment / villa number"
                    rules={[
                      requiredTrimmedRule(
                        'Please enter the apartment / villa number'
                      ),
                    ]}
                  >
                    <Input
                      placeholder="Apartment / villa number"
                      allowClear
                      maxLength={FIELD_MAX_LENGTH}
                      autoComplete="street-number"
                      data-testid="apartment-number-input"
                    />
                  </Form.Item>
                </AddressFields>
                <ConfirmButton
                  type="primary"
                  htmlType="submit"
                  block
                  size="large"
                  loading={mutation.isLoading}
                  disabled={mutation.isLoading}
                  data-testid="submit-address-button"
                  aria-label="Confirm delivery address"
                >
                  Confirm address
                </ConfirmButton>
              </Form>
            </AddressFormWrapper>
          </>
        ) : (
          <>
            <Title>Help us find your delivery location</Title>
            <Subtitle>
              Search for your address or drag the pin to your exact door, then
              confirm.
            </Subtitle>
            <AddressSearchInput token={token} onSelect={handleSelectAddress} />
            <ConfirmButton
              type="primary"
              block
              size="large"
              onClick={handleConfirmPin}
              data-testid="confirm-pin-button"
              aria-label="Continue to address details"
            >
              Confirm pin
            </ConfirmButton>
          </>
        )}
      </BottomSheet>
    </PageContainer>
  )
}
